import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { TextBody } from "@learningblocks/interactives/interactive-infographic-components/lib/TextBody";
import { useUsersnap } from "@labxchange/util-usersnap";
import {
  sanitizeUnsafeHTML,
  sanitizeConfigOptions,
} from "@labxchange/util-sanitization";

import { DropdownHotspot } from "./components/DropdownHotspot";
import { StandardHotspot } from "./components/StandardHotspot";
import {
  ContentBoxTriggerIndicator,
  ContentBoxCorrectnessIndicator,
} from "./components/ContentBoxElements";
import {
  createClickHandler,
  initTooltipDirection,
  checkTextBodySize,
  isDropdownHotspot,
  getHotspotClassNames,
  shouldAppendHotspotText,
  handleTabKeyNavigation,
} from "./utils";
import {
  decodeHtmlEntities,
  formatNavigationText,
  convertSupSubToReadableText,
  getScreenReaderFormatters,
} from "../utils/stringUtils";

import className from "../style.module.scss";

export function Hotspot(props) {
  const intl = useIntl();
  const hotspotRef = useRef();
  const { loadUsersnap } = useUsersnap();

  const [tooltipDirection, setTooltipDirection] = useState("top-right");

  const [textBodyIsSmall, setTextBodyIsSmall] = useState(false);
  const [textBodyWidth, setTextBodyWidth] = useState(0);
  const [textBodyHeight, setTextBodyHeight] = useState(0);

  // When the user presses Escape on a focused hotspot, hide its tooltip while
  // keeping keyboard focus on the trigger (WCAG 1.4.13 — dismiss without moving
  // focus). Reset on blur or pointer re-entry so the tooltip is available again.
  const [tooltipSuppressed, setTooltipSuppressed] = useState(false);

  const ariaLabel = useMemo(() => {
    const lockedText =
      props.locked && !props.data.triggered
        ? intl.formatMessage({ id: "Locked", defaultMessage: "Locked" })
        : "";

    const tooltipText = props.data.tooltipText
      ? decodeHtmlEntities(
          sanitizeUnsafeHTML(
            intl.formatMessage(
              {
                id: props.data.tooltipText,
                defaultMessage: convertSupSubToReadableText(
                  props.data.tooltipText,
                  intl
                ),
              },
              getScreenReaderFormatters(intl)
            ),
            sanitizeConfigOptions.PlainText
          )
        )
      : "";

    let mainText = lockedText || tooltipText;

    // For navigation hotspots, prepend the navigation text BEFORE the label
    if (!lockedText) {
      mainText = formatNavigationText(
        intl,
        props.data.visualOverride,
        mainText
      );
    }

    const hotspotText = shouldAppendHotspotText(props.data)
      ? intl.formatMessage({
          id: "hotspot",
          defaultMessage: "hotspot",
        })
      : "";

    const isViewChanger = props.data.props.type === "view-changer";

    // Content hotspots have their own primary purpose (showing questions, info, etc.)
    // Don't use "triggered/untriggered" for these even if they trigger animations as side effect
    const contentHotspotTypes = [
      "CSCQ",
      "MSCQ",
      "TABLE_QUESTION",
      "learn-more",
      "text-body",
    ];
    const isContentHotspot = contentHotspotTypes.includes(
      props.data.props.type
    );

    // Use "triggered/untriggered" only for hotspots whose PRIMARY purpose is triggering animations
    // (not view-changers, not content hotspots)
    const statusText =
      props.triggersAnimation && !isViewChanger && !isContentHotspot
        ? props.data.triggered &&
          (props.data.postTrigger !== "inactive" ||
            props.data.postTrigger !== "no-change")
          ? intl.formatMessage({ id: "triggered", defaultMessage: "triggered" })
          : intl.formatMessage({
              id: "untriggered",
              defaultMessage: "untriggered",
            })
        : props.data.triggered &&
            (props.data.postTrigger !== "inactive" ||
              props.data.postTrigger !== "no-change")
          ? intl.formatMessage({ id: "checked", defaultMessage: "checked" })
          : intl.formatMessage({
              id: "unchecked",
              defaultMessage: "unchecked",
            });

    // For animation-control, only use the tooltip text (no status or hotspot label)
    if (props.data.props.type === "animation-control") {
      return mainText;
    }

    let finalLabel = hotspotText
      ? `${mainText} ${hotspotText}: [${statusText}]`
      : `${mainText}`;

    // Add animation alt texts only if hotspot is untriggered/unchecked
    // Once triggered, the animation has played so the user doesn't need this information anymore
    if (
      !props.data.triggered &&
      props.animationAltTexts &&
      props.animationAltTexts.length > 0
    ) {
      const animationText = props.animationAltTexts.join(", ");
      finalLabel = `${finalLabel}. Animation: ${animationText}`;
    }

    return finalLabel;
  }, [
    intl,
    props.locked,
    props.data.triggered,
    props.data.tooltipText,
    props.data.postTrigger,
    props.data.visualOverride,
    props.data.props.type,
    props.triggersAnimation,
    props.animationAltTexts,
  ]);

  useEffect(() => {
    initTooltip();

    if (props.data.props.type === "text-body") {
      setTextBodyWidth(0);
      setTextBodyIsSmall(true);
      const timer1 = setTimeout(() => {
        handleTextBodySizeCheck();
      }, props.transitionSpeed * 1000);
      window.addEventListener("resize", handleTextBodySizeCheck);

      return () => {
        clearTimeout(timer1);
        window.removeEventListener("resize", handleTextBodySizeCheck);
      };
    }
  }, []);

  const initTooltip = () => {
    // Force left tooltip for animation control button (positioned at top-right)
    if (props.data.props.type === "animation-control") {
      setTooltipDirection("top-left");
      return;
    }
    const direction = initTooltipDirection(props.data, props.viewData);
    setTooltipDirection(direction);
  };

  const handleTextBodySizeCheck = () => {
    checkTextBodySize(
      props.id,
      props.data,
      props.isDiabledBlurOnSmall,
      setTextBodyWidth,
      setTextBodyHeight,
      setTextBodyIsSmall
    );
  };

  const handleClick = createClickHandler({
    ...props,
    loadUsersnap,
  });

  const renderHotSpot = () => {
    return (
      <StandardHotspot
        {...props}
        tooltipDirection={tooltipDirection}
        tooltipSuppressed={tooltipSuppressed}
      />
    );
  };

  if (isDropdownHotspot(props.data)) {
    return <DropdownHotspot {...props} tooltipDirection={tooltipDirection} />;
  }

  return (
    <>
      {props.data.props.type === "content-box" && (
        <ContentBoxTriggerIndicator
          selectedHotspot={props.selectedHotspot}
          id={props.id}
        />
      )}
      {props.data.props.type === "content-box" && (
        <ContentBoxCorrectnessIndicator
          isCorrect={props.isCorrect}
          hotspotRef={hotspotRef}
          handleClick={() => {
            props.setLatestHotspotRef(hotspotRef);
            handleClick();
          }}
        />
      )}
      <button
        id={`hotspotBtn_${props.id}`}
        ref={hotspotRef}
        className={getHotspotClassNames(props, className)}
        disabled={
          (props.data.triggered && props.data.postTrigger === "inactive") ||
          (props.locked && !props.data.triggered)
        }
        tabIndex={
          props.data.visibility_dependencies.views.includes(
            props.currentView
          ) || props.data.visibility_dependencies.views.length === 0
            ? 0
            : -1
        }
        onMouseDown={(e) => {
          // A view-changer unmounts itself on activation (visibility is
          // per-view), so the browser's mousedown focus default action must
          // be canceled and focus set explicitly — otherwise Chrome's
          // deferred focus processing resolves against the removed element
          // and drops focus to the iframe body ("main frame"), trampling the
          // view-transition focus restoration (WCAG 2.4.3). This mirrors the
          // keydown path below, which already prevents the default.
          if (props.data.props.type === "view-changer") {
            e.preventDefault();
            hotspotRef.current?.focus();
          }
          props.setLatestHotspotRef(hotspotRef);
          handleClick();
        }}
        onMouseEnter={() => setTooltipSuppressed(false)}
        onBlur={() => setTooltipSuppressed(false)}
        onKeyDown={(e) => {
          // Handle Tab key navigation for triggered hotspots
          if (e.key === "Tab") {
            handleTabKeyNavigation(
              e,
              props.id,
              props.data,
              props.hotspotOrder,
              props.allHotspots,
              props.currentView
            );
            return;
          }

          // Handle Enter and Space for activation
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            props.setLatestHotspotRef(hotspotRef);
            handleClick();
          }
          if (e.key === "Escape") {
            // Dismiss the tooltip without moving focus (WCAG 1.4.13). Keep the
            // activeElement guard introduced in #4120 so this never interferes
            // with a modal's own Escape-to-close, and so focus is NOT dropped to
            // the iframe body ("main frame") the way the previous blur() did.
            if (
              hotspotRef.current &&
              document.activeElement === hotspotRef.current
            ) {
              setTooltipSuppressed(true);
            }
          }
        }}
        aria-label={ariaLabel}
      >
        {props.data.props.type === "text-body" ? (
          <TextBody
            id={props.id}
            textBodyIsSmall={textBodyIsSmall}
            textBodyWidth={textBodyWidth}
            textBodyHeight={textBodyHeight}
            text={props.data.props.text}
            theme={props.data.props.theme}
          />
        ) : null}
        {props.data.props.type === "text-body" && !textBodyIsSmall
          ? null
          : renderHotSpot()}
      </button>
    </>
  );
}

Hotspot.propTypes = {
  data: PropTypes.shape({
    props: PropTypes.shape({
      type: PropTypes.string.isRequired,
    }).isRequired,
    triggered: PropTypes.bool.isRequired,
    postTrigger: PropTypes.string.isRequired,
  }).isRequired,
  transitionSpeed: PropTypes.number.isRequired,
  viewData: PropTypes.shape({
    x_offset: PropTypes.number.isRequired,
    y_offset: PropTypes.number.isRequired,
    size: PropTypes.shape({
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }),
  }).isRequired,
  id: PropTypes.string.isRequired,
  locked: PropTypes.bool.isRequired,
  hide: PropTypes.bool.isRequired,
  showGrid: PropTypes.bool.isRequired,
  idle: PropTypes.bool.isRequired,
  isDiabledBlurOnSmall: PropTypes.bool.isRequired,
  selectedHotspot: PropTypes.number,
  usersnapProjectId: PropTypes.string,
  triggersAnimation: PropTypes.bool,
  animationAltTexts: PropTypes.arrayOf(PropTypes.string),
  hotspotOrder: PropTypes.array,
  allHotspots: PropTypes.object,
  currentView: PropTypes.number,
};
